import _ from "lodash";
import Pusher, { Channel } from "pusher-js/with-encryption";

import { BASE_URL } from "config/urls";
import { getEnvironmentVariables } from "utils/environment";
import { getToken } from "utils/storage";

const PUSHER_KEY = getEnvironmentVariables().VITE_PUSHER_KEY;
const PUSHER_CLUSTER = getEnvironmentVariables().VITE_PUSHER_CLUSTER;

// Channel names
const GLOBAL_USER_SPECIFIC_CHANNEL_NAME = ({ userId }: { userId: number }) =>
  `private-encrypted-global-user-specific-${userId}`;

// Event names
const ZIP_READY_EVENT = "zip-ready-event";
const ZIP_FAILED_EVENT = "zip-failed-event";
const TASK_CHAT = "chat-message-update-event";
const ASSET_MANAGEMENT_EXCEL_PROCESSING_SUCCEEDED =
  "asset-management-excel-processing-succeeded-event";
const ASSET_MANAGEMENT_EXCEL_PROCESSING_FAILED =
  "asset-management-excel-processing-failed-event";
const UNDERWRITING_READY_EVENT = "underwriting-proforma-ready-event";

const OPENAI_PROMPT_RESPONSE_READY_EVENT = "openai-prompt-response-ready-event";
const DEAL_PROPERTY_UPDATE_EVENT = "deal-property-update-event";
const NEW_NOTIFICATION_EVENT = "new-notification-event";
const OPENAI_TABLE_RESPONSE_SUCCEEDED_EVENT =
  "openai-table-response-succeeded-event";
const OPENAI_TABLE_RESPONSE_FAILED_EVENT = "openai-table-response-failed-event";

const DEAL_TABLE_UPDATE_SUCCEEDED_EVENT = "deal-table-update-succeeded-event";
const TABLE_UPDATE_SUCCEEDED_EVENT = "table-update-succeeded-event";
const TABLE_OR_DEAL_TABLE_UPDATE_FAILED_EVENT =
  "table-or-deal-table-update-failed-event";
const DEAL_COMPANY_IMPORT_TABLE_SHEET_NAME_NOT_FOUND_EVENT =
  "deal-company-import-table-sheet-name-not-found-event";
const DEAL_COMPANY_IMPORT_TABLE_UPDATE_FAILED_EVENT =
  "deal-company-import-table-update-failed-event";
const REPORT_PACKAGE_DETAILS_INVALIDATE_SUCCEEDED_EVENT =
  "report-package-details-invalidate-succeeded-event";
const SUPERUSER_DEALS_AND_LINKED_FIELDS_RETRIEVE_ATTEMPT_STATUS_UPDATE_EVENT =
  "superuser-deals-and-linked-fields-retrieve-attempt-status-update-event";
const SUPERUSER_LINKED_FIELD_ADDITION_STATUS_UPDATE_EVENT =
  "superuser-linked-field-addition-status-update-event";
const BULK_DEAL_IMPORT_SUCCEEDED_EVENT = "deal-import-create-succeeded-event";
const BULK_DEAL_IMPORT_EVENT_FAILED = "deal-import-create-failed-event";
const CHECKLIST_FILE_IMPORT_SUCCEEDED_EVENT =
  "company-package-file-import-succeeded-event";
const CHECKLIST_FILE_IMPORT_FAILED_EVENT =
  "company-package-file-import-failed-event";
const COMPANY_SECTION_AUTOMATIC_FILL_PROCESSING_SUCCEDED_EVENT =
  "company-section-automatic-fill-processing-succeeded-event";
const COMPANY_SECTION_AUTOMATIC_FILL_PROCESSING_FAILED_EVENT =
  "company-section-automatic-fill-processing-failed-event";
const MEMO_SECTION_AI_PROMPT_PROCESSING_FAILED_EVENT =
  "memo-section-ai-prompt-processing-failed-event";
const MEMO_SECTION_AI_PROMPT_PROCESSING_SUCCEDEED_EVENT =
  "memo-section-ai-prompt-processing-succeeded-event";
const COMPANY_PACKAGE_SNAPSHOT_PROCESSING_FAILED_EVENT =
  "company-package-snapshot-processing-failed-event";
const COMPANY_PACKAGE_SNAPSHOT_PROCESSING_SUCCEEDED_EVENT =
  "company-package-snapshot-processing-succeeded-event";
const PROFORMA_PDF_PROCESSING_FAILED_EVENT =
  "proforma-pdf-processing-failed-event";
const PROFORMA_PDF_PROCESSING_SUCCEEDED_EVENT =
  "proforma-pdf-processing-succeeded-event";

const MEMO_PDF_PROCESSING_FAILED_EVENT = "memo-pdf-processing-failed-event";
const MEMO_PDF_PROCESSING_SUCCEEDED_EVENT =
  "memo-pdf-processing-succeeded-event";

class PusherManager {
  pusherClient: Pusher | null;
  channels: { [channelName: string]: Channel | null };

  constructor() {
    this.pusherClient = null;
    this.channels = {};
  }
  initPusherClient = () => {
    if (!PUSHER_KEY || !PUSHER_CLUSTER) {
      throw Error;
    }

    return new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      channelAuthorization: {
        transport: "ajax",
        endpoint: `${BASE_URL}v2/integrations/pusher/authenticate-client/`,
        headers: {
          Authorization: `jwt ${getToken()}`,
        },
      },
    });
  };

  getPusherClient = () => {
    if (_.isNil(this.pusherClient)) {
      this.pusherClient = this.initPusherClient();
    }

    return this.pusherClient;
  };

  getAndCreateChannelIfDoesNotExist = ({
    channelName,
  }: {
    channelName: string;
  }): Channel => {
    let channel = this.channels[channelName];

    if (_.isNil(channel)) {
      const pusherClient = this.getPusherClient();
      channel = pusherClient.subscribe(channelName);
      this.channels[channelName] = channel;
    }

    return channel;
  };

  bindToZipReadyEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: () => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = ZIP_READY_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToZipFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: (data: any) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = ZIP_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToTaskChat = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: (data: any) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = TASK_CHAT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToAssetManagementExcelProcessingSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: (data: any) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = ASSET_MANAGEMENT_EXCEL_PROCESSING_SUCCEEDED;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToAssetManagementExcelProcessingFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = ASSET_MANAGEMENT_EXCEL_PROCESSING_FAILED;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  disconnect = () => {
    if (_.isNil(this.pusherClient)) {
      return;
    }

    this.pusherClient.disconnect();
    this.pusherClient = null;
  };

  bindToUnderwritingProformaReadyEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ deal_id }: { deal_id: number }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = UNDERWRITING_READY_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToOpenAIPromptResponseReadyEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      deal_id,
      openai_document_assistant_id,
      prompt_response_id,
    }: {
      deal_id: number;
      openai_document_assistant_id: number;
      prompt_response_id: number;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = OPENAI_PROMPT_RESPONSE_READY_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToDealPropertyUpdateEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ deal_id }: { deal_id: number }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = DEAL_PROPERTY_UPDATE_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToNewNotificationEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: () => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = NEW_NOTIFICATION_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToTableResponseSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      openai_table_response_id,
    }: {
      openai_table_response_id: number;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = OPENAI_TABLE_RESPONSE_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToTableResponseFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: () => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = OPENAI_TABLE_RESPONSE_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToDealTableUpdateSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      deal_id,
      table_id,
      table_name,
    }: {
      deal_id: number;
      table_id: number;
      table_name: string;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = DEAL_TABLE_UPDATE_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToTableUpdateSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      table_id,
      table_name,
    }: {
      table_id: number;
      table_name: string;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = TABLE_UPDATE_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToBulkDealImportSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: () => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = BULK_DEAL_IMPORT_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToBulkDealImportFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = BULK_DEAL_IMPORT_EVENT_FAILED;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };
  bindToMemoSectionAIExtractionProcessingSuccededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      deal_id,
      memo_id,
      memo_section_id,
    }: {
      deal_id: number;
      memo_id: number;
      memo_section_id: number;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = MEMO_SECTION_AI_PROMPT_PROCESSING_SUCCEDEED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToMemoSectionAIExtractionProcessingFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = MEMO_SECTION_AI_PROMPT_PROCESSING_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToCompanySectionAutomaticFillProcessingSuccededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      deal_id,
      company_package_id,
      automatic_fill_id,
      company_tab_id,
    }: {
      deal_id: number;
      company_package_id: number;
      automatic_fill_id: number;
      company_tab_id: number;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = COMPANY_SECTION_AUTOMATIC_FILL_PROCESSING_SUCCEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToCompanySectionAutomaticFillProcessingFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = COMPANY_SECTION_AUTOMATIC_FILL_PROCESSING_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToChecklistFileImportSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      company_package_file_import_id,
    }: {
      company_package_file_import_id: number;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = CHECKLIST_FILE_IMPORT_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToChecklistFileImportFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = CHECKLIST_FILE_IMPORT_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToTableOrDealTableUpdateFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ details }: { details: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = TABLE_OR_DEAL_TABLE_UPDATE_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToDealCompanyImportTableSheetNameNotFoundEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ details }: { details: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = DEAL_COMPANY_IMPORT_TABLE_SHEET_NAME_NOT_FOUND_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToDealCompanyImportTableUpdateFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ details }: { details: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = DEAL_COMPANY_IMPORT_TABLE_UPDATE_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToReportPackageDetailsInvalidateSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      report_package_id,
      report_package_name,
    }: {
      report_package_id: number;
      report_package_name: string;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = REPORT_PACKAGE_DETAILS_INVALIDATE_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToCompanyPackageSnapshotProcessingFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = COMPANY_PACKAGE_SNAPSHOT_PROCESSING_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToCompanyPackageSnapshotProcessingSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: () => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = COMPANY_PACKAGE_SNAPSHOT_PROCESSING_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToSuperuserDealsAndLinkedFieldsRetrieveAttemptStatusUpdateEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      retrieve_attempt_id,
    }: {
      retrieve_attempt_id: number;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName =
      SUPERUSER_DEALS_AND_LINKED_FIELDS_RETRIEVE_ATTEMPT_STATUS_UPDATE_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToSuperuserLinkedFieldAdditionStatusUpdateEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      successful,
      error,
    }: {
      successful: boolean;
      error: string;
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = SUPERUSER_LINKED_FIELD_ADDITION_STATUS_UPDATE_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToProformaPDFGenerationSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: () => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = PROFORMA_PDF_PROCESSING_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToProformaPDFGenerationFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = PROFORMA_PDF_PROCESSING_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToMemoPDFGenerationSucceededEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({
      data,
    }: {
      data: {
        deal_id: number;
        memo_id: number;
        memo_pdf_id: number;
      };
    }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = MEMO_PDF_PROCESSING_SUCCEEDED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };

  bindToMemoPDFGenerationFailedEvent = ({
    userId,
    eventHandler,
  }: {
    userId: number;
    eventHandler: ({ detail }: { detail: string }) => void;
  }) => {
    const channelName = GLOBAL_USER_SPECIFIC_CHANNEL_NAME({ userId });

    const channel = this.getAndCreateChannelIfDoesNotExist({ channelName });
    const eventName = MEMO_PDF_PROCESSING_FAILED_EVENT;

    channel.bind(eventName, eventHandler);

    return () => {
      channel.unbind(eventName, eventHandler);
    };
  };
}

const pusherManager = new PusherManager();

export default pusherManager;
